import {
  useErrorHandlingMutation,
  useErrorHandlingQuery,
} from '@backoffice-frontend/data-fetching'
import { useMoiaSuccessNotification } from '@backoffice-frontend/patterns'
import type { useBackofficeLoggedInUser } from '@backoffice-frontend/user-management-ui'
import { useRefreshPermissions } from '@backoffice-frontend/user-management-ui'
import {
  ActionItemGroup,
  ActionItemMultiSelect,
  Button,
  Callout,
  LoadingIndicator,
  SelectionMenu,
  type useSelectionMenu,
} from '@moia-dev/pace-web'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useAccountBadgeGroupSwitcherQuery,
  useSwitchGroupsMutation,
} from './AccountBadgeGroupSwitcher.hook'

type AccountBadgeGroupSwitcherProps = {
  selectionMenuProps: ReturnType<typeof useSelectionMenu>
  user: NonNullable<ReturnType<typeof useBackofficeLoggedInUser>['user']>
}

export const AccountBadgeGroupSwitcher = ({
  selectionMenuProps,
  user,
}: AccountBadgeGroupSwitcherProps) => {
  const { t } = useTranslation()
  const refreshPermissions = useRefreshPermissions()
  const [query, setQuery] = useState('')
  const userGroups = user.timedGroups.map(group => group.groupName)
  const [selected, setSelected] = useState<string[]>(userGroups)
  const [switchGroups] = useErrorHandlingMutation(useSwitchGroupsMutation)
  const { data, loading } = useErrorHandlingQuery(
    useAccountBadgeGroupSwitcherQuery,
    { variables: {} },
  )
  const { enqueueMoiaSuccessNotification } = useMoiaSuccessNotification()

  const handleSubmit = async () => {
    await switchGroups({
      variables: { input: { groupName: null, groupsList: selected } },
    })
    enqueueMoiaSuccessNotification(
      t('authenticatedAppView_groupSwitchedSuccess'),
    )
    await refreshPermissions({ skipSuccessToast: true })
  }

  const filteredGroups = (data?.groups ?? [])
    .filter(group => group.assignedOrganizations?.includes(user.organizationId))
    .filter(option =>
      query.length > 0
        ? option.displayName.toLowerCase().includes(query.toLowerCase()) ||
          option.name.toLowerCase().includes(query.toLowerCase())
        : true,
    )

  const currentlyAssignedGroups = filteredGroups.filter(option =>
    userGroups.includes(option.name),
  )
  const notAssignedGroups = filteredGroups.filter(
    option => !userGroups.includes(option.name),
  )

  return (
    <SelectionMenu
      {...selectionMenuProps}
      title={t('authenticatedAppView_groupSwitcher_header')}
      caption={t('authenticatedAppView_groupSwitcher_caption')}
      footer={
        <div
          css={theme => ({
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1),
          })}
        >
          <div css={{ width: 340 }}>
            <Callout hideDismiss>
              {t('authenticatedAppView_groupSwitcher_callout')}
            </Callout>
          </div>

          <div css={theme => ({ display: 'flex', gap: theme.spacing(1) })}>
            <Button
              css={{ flex: 1 }}
              disabled={selected.length === 0}
              label={t(
                'authenticatedAppView_groupSwitcher_clearButtonLabel ({{currentSelected}})',
                { currentSelected: selected.length },
              )}
              onClick={() => setSelected([])}
              variant="secondary"
              size="compact"
            />
            <Button
              css={{ flex: 1 }}
              label={t('common_apply_label')}
              onClick={handleSubmit}
              size="compact"
            />
          </div>
        </div>
      }
      query={query}
      onQueryChange={setQuery}
    >
      {loading && (
        <div css={theme => ({ padding: theme.spacing(1), width: 330 })}>
          <LoadingIndicator />
        </div>
      )}
      {!loading && filteredGroups.length === 0 && (
        <div css={theme => ({ padding: theme.spacing(1), width: 330 })}>
          {t('common_noResults_title')}
        </div>
      )}
      {currentlyAssignedGroups.length > 0 && (
        <ActionItemGroup
          label={t('authenticatedAppView_groupSwitcherCurrentGroups_label')}
        >
          {currentlyAssignedGroups.map(option => (
            <ActionItemMultiSelect
              style={{ width: 330 }}
              key={option.name}
              selected={selected.includes(option.name)}
              onClick={() => {
                if (selected.includes(option.name)) {
                  setSelected(selected.filter(item => item !== option.name))
                } else {
                  setSelected([...selected, option.name])
                }
              }}
              label={option.displayName}
            />
          ))}
        </ActionItemGroup>
      )}
      {notAssignedGroups.length > 0 && (
        <ActionItemGroup
          label={t('authenticatedAppView_groupSwitcherGroups_label')}
        >
          {notAssignedGroups.map(option => (
            <ActionItemMultiSelect
              style={{ width: 330 }}
              key={option.name}
              selected={selected.includes(option.name)}
              onClick={() => {
                if (selected.includes(option.name)) {
                  setSelected(selected.filter(item => item !== option.name))
                } else {
                  setSelected([...selected, option.name])
                }
              }}
              label={option.displayName}
            />
          ))}
        </ActionItemGroup>
      )}
    </SelectionMenu>
  )
}
